import React, { useEffect, useState } from 'react';
import { Contact, RefreshCw, Eye, Loader2, PlugZap, History, RotateCcw, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SyncResultPanel } from './sync/SyncResultPanel';
import { SyncHistory } from './sync/SyncHistory';

const SCOPE = 'https://www.googleapis.com/auth/contacts';

function DelegationGuide({ clientId }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState('');
  const copy = (txt, k) => { navigator.clipboard?.writeText(txt); setCopied(k); setTimeout(() => setCopied(''), 1500); };
  return (
    <div className="rounded border p-3 text-sm" style={{ borderColor: 'rgba(0,0,0,0.1)', background: 'rgba(0,0,0,0.02)' }}>
      <button data-testid="gc-guide-toggle" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 font-title" style={{ color: 'var(--navy)' }}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} One-time setup: domain-wide delegation (Workspace super-admin)
      </button>
      {open && (
        <ol className="list-decimal pl-5 mt-2 space-y-1 text-xs" style={{ color: 'var(--ink)' }}>
          <li>In Google Cloud, enable the <b>People API</b> (APIs &amp; Services → Library) for the project that owns the service account.</li>
          <li>Open <b>admin.google.com → Security → Access and data control → API controls → Manage Domain Wide Delegation → Add new</b>.</li>
          <li>Client ID: {clientId ? (
            <code className="px-1 rounded" style={{ background: 'white' }}>{clientId}</code>
          ) : <i>save a service-account key above to see it</i>}
            {clientId && <button data-testid="gc-copy-client-id" onClick={() => copy(clientId, 'id')} className="ml-1 inline-flex align-middle" title="Copy">{copied === 'id' ? <Check size={12} /> : <Copy size={12} />}</button>}
          </li>
          <li>OAuth scope: <code className="px-1 rounded" style={{ background: 'white' }}>{SCOPE}</code>
            <button data-testid="gc-copy-scope" onClick={() => copy(SCOPE, 'scope')} className="ml-1 inline-flex align-middle" title="Copy">{copied === 'scope' ? <Check size={12} /> : <Copy size={12} />}</button>
          </li>
          <li>Authorise, wait a few minutes, then enter the mailbox below and press <b>Test connection</b>.</li>
        </ol>
      )}
    </div>
  );
}

export function GoogleContactsCard({ cfg, setField }) {
  const { testGoogleContacts, previewContactsSync, runContactsSync, getContactsSyncLog, getContactsSyncLogDetail, resetContactsSyncState } = useAuth();
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState(null);
  const [test, setTest] = useState(null);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const ready = cfg.service_account_set && cfg.contacts_enabled && cfg.contacts_subject;
  const disabled = !!busy || !ready;

  const loadLog = () => getContactsSyncLog(10).then(setLog).catch(() => setLog([]));
  useEffect(() => { loadLog(); /* eslint-disable-next-line */ }, []);

  const run = async (kind) => {
    setBusy(kind); if (kind !== 'test') setRes(null); else setTest(null);
    try {
      if (kind === 'test') setTest(await testGoogleContacts());
      else { setRes(kind === 'preview' ? await previewContactsSync() : await runContactsSync()); if (kind === 'sync') loadLog(); }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Request failed';
      if (kind === 'test') setTest({ error: msg }); else setRes({ error: msg });
    }
    setBusy('');
  };

  const reset = async () => {
    if (!window.confirm('Forget the last-sync snapshot for Google Contacts?')) return;
    await resetContactsSyncState(); setRes(null);
  };

  const btn = (bg, fg) => ({ background: bg, color: fg, opacity: disabled ? 0.6 : 1 });
  return (
    <div className="bg-white rounded-lg p-5 border shadow-sm" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="google-contacts-card">
      <div className="flex items-center gap-2 mb-3"><Contact size={16} style={{ color: 'var(--navy)' }} /><h3 className="font-title text-lg" style={{ color: 'var(--navy)' }}>Google Contacts — club mailbox</h3></div>
      <p className="text-xs mb-3" style={{ color: 'var(--olive)' }}>Keeps every contact and member in the club's Google Contacts, labelled by member class (e.g. "Members: Financial"). Names, email, phones, address and notes sync; class labels are written from the app to Google only.</p>
      <DelegationGuide clientId={cfg.client_id} />
      <div className="flex items-center gap-3 mt-3">
        <Switch data-testid="gc-enabled-toggle" checked={!!cfg.contacts_enabled} onCheckedChange={(v) => setField('contacts_enabled', v)} />
        <span className="text-sm">Enable Google Contacts sync</span>
      </div>
      <div className="grid md:grid-cols-3 gap-2 mt-3">
        <div>
          <label className="text-xs" style={{ color: 'var(--olive)' }}>Workspace mailbox to sync</label>
          <Input data-testid="gc-subject" value={cfg.contacts_subject || ''} onChange={(e) => setField('contacts_subject', e.target.value)} placeholder="secretary@yourdomain.org.au" />
        </div>
        <div>
          <label className="text-xs" style={{ color: 'var(--olive)' }}>Label prefix</label>
          <Input data-testid="gc-prefix" value={cfg.contacts_group_prefix || ''} onChange={(e) => setField('contacts_group_prefix', e.target.value)} placeholder="Members" />
        </div>
        <div>
          <label className="text-xs" style={{ color: 'var(--olive)' }}>Direction</label>
          <Select value={cfg.contacts_sync_mode || 'two_way'} onValueChange={(v) => setField('contacts_sync_mode', v)}>
            <SelectTrigger data-testid="gc-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="two_way">Two-way — last modified wins</SelectItem>
              <SelectItem value="push">Push only — app is the master</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <button data-testid="gc-test" onClick={() => run('test')} disabled={disabled} className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-title" style={btn('var(--navy)', 'var(--cream)')}>
          {busy === 'test' ? <Loader2 className="animate-spin" size={14} /> : <PlugZap size={14} />} Test connection
        </button>
        <button data-testid="gc-preview" onClick={() => run('preview')} disabled={disabled} className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-title" style={btn('var(--sage)', 'var(--ink)')}>
          {busy === 'preview' ? <Loader2 className="animate-spin" size={14} /> : <Eye size={14} />} Preview changes
        </button>
        <button data-testid="gc-sync" onClick={() => run('sync')} disabled={disabled} className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-title" style={btn('var(--maroon)', 'var(--cream)')}>
          {busy === 'sync' ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Sync contacts now
        </button>
        <button data-testid="gc-reset-state" onClick={reset} className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm" style={{ color: 'var(--olive)', border: '1px solid rgba(0,0,0,0.15)' }}>
          <RotateCcw size={14} /> Forget snapshot
        </button>
        <button data-testid="gc-log-toggle" onClick={() => setShowLog((v) => !v)} className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm" style={{ color: 'var(--navy)', border: '1px solid rgba(0,0,0,0.15)' }}>
          <History size={14} /> History ({log.length})
        </button>
      </div>
      {!ready && <p className="text-xs mt-2" style={{ color: 'var(--olive)' }} data-testid="gc-not-ready">Add a service-account key, enable the sync, enter the mailbox and <b>Save Google settings</b> to activate these buttons.</p>}
      {test && (
        <p className="text-sm mt-2" data-testid="gc-test-result" style={{ color: test.error ? '#8a1f11' : '#1a7f37' }}>
          {test.error || `Connected to ${test.subject}: ${test.total_contacts} contacts, ${test.groups.length} labels${test.groups.length ? ` (${test.groups.slice(0, 5).join(', ')}${test.groups.length > 5 ? '…' : ''})` : ''}.`}
        </p>
      )}
      <div className="flex items-center gap-3 mt-3">
        <Switch data-testid="gc-autosync-toggle" checked={!!cfg.auto_sync_contacts} onCheckedChange={(v) => setField('auto_sync_contacts', v)} />
        <span className="text-sm">Nightly auto-sync (then Save)</span>
      </div>
      {cfg.last_contacts_sync_at && <p className="text-xs mt-2" style={{ color: 'var(--olive)' }} data-testid="gc-last-sync">Last sync: {new Date(cfg.last_contacts_sync_at).toLocaleString()}</p>}
      <SyncResultPanel res={res} remoteName="Google" testid="gc-sync-result" />
      {showLog && <SyncHistory log={log} remoteName="Google" loadDetail={getContactsSyncLogDetail} testid="gc-log" />}
    </div>
  );
}
